import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  App, Button, Card, Select, Space, Table, Tag, Tooltip, Typography,
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, RedoOutlined, SyncOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import CreateJobModal from '@components/CreateJobModal';
import {
  getPerceptionRefreshScenes,
  getPerceptionRefreshTaskType,
  rearchivePerceptionRefreshJob,
  type PerceptionRefreshScene,
  type PerceptionRefreshSceneStatus,
} from '@/apis/simulation';

const { Title, Text } = Typography;

const formatTime = (val?: string | null) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-');

const SCENE_STATUS_META: Record<PerceptionRefreshSceneStatus, { color: string; label: string }> = {
  succeeded: { color: 'success', label: '已归档' },
  failed: { color: 'error', label: '失败' },
  no_output: { color: 'warning', label: '无产物' },
};

// 「重跑失败场景」按 task 状态反查场景：取消(1) / 执行异常(25) / 系统异常(30) / coredump(40)。
// task 成功但「无产物 / 缺节点」属于数据问题，重跑也不会好，不在此列。
const RERUN_TASK_STATUSES = [1, 25, 30, 40];

/**
 * 感知数据刷写某个 Job 的场景明细：目录名、状态、目标 BOS 路径、拷贝文件数、原因。
 * 重跑失败场景会提交新的 PE_DATA Job、分配新版本号，不补进当前版本。
 */
export default function PerceptionRefreshScenesPage() {
  const { jobId: jobIdParam } = useParams<{ jobId: string }>();
  const jobId = Number(jobIdParam);
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [data, setData] = useState<PerceptionRefreshScene[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  // 游标翻页：cursors[i] = 第 i+1 页的 afterId（第一页 undefined）；上一页 = 弹栈，不用 OFFSET。
  const [cursors, setCursors] = useState<(number | undefined)[]>([undefined]);
  const [nextAfterId, setNextAfterId] = useState<number | null>(null);
  const [status, setStatus] = useState<PerceptionRefreshSceneStatus | undefined>();
  const [taskType, setTaskType] = useState<{ id: number; name: string } | null>(null);
  const [rerunOpen, setRerunOpen] = useState(false);
  const [rearchiving, setRearchiving] = useState(false);

  const handleRearchive = async () => {
    setRearchiving(true);
    try {
      await rearchivePerceptionRefreshJob(jobId);
      message.success('已提交重新归档，后台执行中，稍后刷新查看');
    } catch (err) {
      // 业务错误由 axios 拦截器弹出原因。
      console.error('提交重新归档失败:', err);
    } finally {
      setRearchiving(false);
    }
  };

  const load = useCallback(async () => {
    if (!Number.isFinite(jobId)) return;
    setLoading(true);
    // 只在第一页计数（首次进入 / 切换状态 / 刷新），往后翻页沿用该值，省掉每页一次 COUNT。
    const isFirstPage = cursors.length === 1;
    try {
      const res = await getPerceptionRefreshScenes(jobId, {
        status, pageSize, afterId: cursors[cursors.length - 1], withTotal: isFirstPage,
      });
      setData(res.list);
      setNextAfterId(res.nextAfterId);
      if (res.total !== null) setTotal(res.total);
    } catch (err) {
      console.error('加载场景明细失败:', err);
      message.error('加载场景明细失败');
    } finally {
      setLoading(false);
    }
  }, [jobId, status, pageSize, cursors, message]);

  useEffect(() => {
    void load();
  }, [load]);

  const openRerun = async () => {
    try {
      const tt = taskType ?? await getPerceptionRefreshTaskType().then((r) => ({ id: r.taskTypeId, name: r.name }));
      setTaskType(tt);
      setRerunOpen(true);
    } catch (err) {
      console.error('获取感知数据刷写任务类型失败:', err);
    }
  };

  const columns: ColumnsType<PerceptionRefreshScene> = [
    { title: 'Task ID', dataIndex: 'taskId', width: 100 },
    { title: '场景 ID', dataIndex: 'sceneId', width: 100 },
    {
      title: '目录名（pkl_stem）',
      dataIndex: 'pklStem',
      width: 360,
      render: (v?: string | null) => (v ? <Text copyable>{v}</Text> : '-'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (s: PerceptionRefreshSceneStatus) => {
        const meta = SCENE_STATUS_META[s];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : s;
      },
    },
    { title: '文件数', dataIndex: 'copiedFiles', width: 80 },
    {
      title: '目标 BOS 路径',
      dataIndex: 'bosPath',
      width: 420,
      ellipsis: true,
      render: (v?: string | null) => (v ? <Text copyable ellipsis={{ tooltip: v }}>{v}</Text> : '-'),
    },
    {
      title: '原因 / 说明',
      dataIndex: 'reason',
      width: 260,
      ellipsis: true,
      render: (v?: string | null) => (v ? <Tooltip title={v}>{v}</Tooltip> : '-'),
    },
    { title: '更新时间', dataIndex: 'updateTime', width: 170, render: formatTime },
  ];

  return (
    <Card>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/prediction-update?type=perception')}>
            返回
          </Button>
          <Title level={4} style={{ margin: 0 }}>数据更新 · 感知数据 · Job {jobIdParam} 场景明细</Title>
        </Space>
        <Space>
          <Tooltip title="对已结束、但还没归档成功的场景补做归档：重新拷贝引擎产物（引擎产物保留 30 天）；执行异常 / 崩溃的场景也会检查产物，完整就照常归档，没有就写明失败原因。Job 已结束时会重新汇总版本计数。后台执行，稍后刷新查看">
            <Button icon={<SyncOutlined />} loading={rearchiving} onClick={() => void handleRearchive()}>
              重新归档
            </Button>
          </Tooltip>
          <Tooltip title="把本 Job 里执行失败 / 取消的场景提交为新的刷写任务（分配新版本号）">
            <Button icon={<RedoOutlined />} onClick={() => void openRerun()}>重跑失败场景</Button>
          </Tooltip>
        </Space>
      </Space>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="场景状态"
          allowClear
          style={{ width: 140 }}
          value={status}
          onChange={(v) => {
            setCursors([undefined]);
            setStatus(v);
          }}
          options={Object.entries(SCENE_STATUS_META).map(([value, m]) => ({ value, label: m.label }))}
        />
        <Button icon={<ReloadOutlined />} onClick={() => setCursors([undefined])}>刷新</Button>
        <Text type="secondary">场景明细在 task 结束后写入，运行中的 task 暂不显示。</Text>
      </Space>
      <Table
        rowKey="taskId"
        loading={loading}
        columns={columns}
        dataSource={data}
        scroll={{ x: 'max-content' }}
        pagination={false}
      />
      <Space style={{ marginTop: 16, width: '100%', justifyContent: 'flex-end' }} wrap>
        <Text type="secondary">共 {total} 条 · 第 {cursors.length} 页</Text>
        <Button
          disabled={cursors.length <= 1 || loading}
          onClick={() => setCursors((c) => c.slice(0, -1))}
        >
          上一页
        </Button>
        <Button
          disabled={nextAfterId === null || loading}
          onClick={() => nextAfterId !== null && setCursors((c) => [...c, nextAfterId])}
        >
          下一页
        </Button>
        <Select
          value={pageSize}
          style={{ width: 110 }}
          onChange={(v) => {
            setCursors([undefined]);
            setPageSize(v);
          }}
          options={[20, 50, 100, 200].map((n) => ({ value: n, label: `${n} 条/页` }))}
        />
      </Space>
      {taskType && rerunOpen && (
        <CreateJobModal
          open={rerunOpen}
          onClose={() => setRerunOpen(false)}
          onSuccess={() => {
            setRerunOpen(false);
            message.success('已提交重跑任务，新版本见数据更新 · 感知数据列表');
          }}
          perceptionRefreshTaskType={taskType}
          byTaskQuery={{
            jobId,
            filters: { statuses: RERUN_TASK_STATUSES },
            filterEntries: [{ key: 'statuses', label: 'Task 状态', valueLabel: '取消 / 执行异常 / 系统异常 / coredump' }],
          }}
        />
      )}
    </Card>
  );
}
